import { MENU_NAMES } from 'texts/menuNames'
import { Container } from 'ui/molecules/Container'
import { Table } from 'ui/molecules/Table'
import {
  BOOKING_HISTORY_COLUMN_INDEX,
  BOOKING_STATUS,
  TABLE_HEAD_ITEMS,
} from './constants'
import { PAGINATION_DROPDOWN_LABEL } from 'texts/uiTexts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Typography, Modal, Tooltip, DaysPicker } from '@frontend/design_system'
import { InfoIcon, MaximizeIcon, ModalCloseIcon } from 'ui/icons'
import { useApiRequest } from 'hooks/useApiRequest'
import { bookingHistoryApi, BookingHistoryTypes } from 'api'
import { prepareColumnsWidth } from './utils'
import { useCompanyId } from 'hooks/useCompanyId'
import styles from './styles.module.scss'
import { isOperatorSelector } from 'redux/login/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { breadcrumbs } from 'constants/breadcrumbs'
import { BOOKING_HISTORY_TOOLTIP } from 'texts/bookingHistory'
import { selectedRangeSelector } from 'redux/bookingHistory/selectors'
import {
  DATEPICKER_DEFAULT_FORMAT,
  STOP_SALES_DATE_FORMAT,
} from 'constants/date'
import { addDays, format } from 'date-fns'
import { setSelectedBookingHistoryRange } from 'redux/bookingHistory/slice'
import { Pagination } from 'ui/molecules/Pagination'
import { useFilterSettings } from 'hooks/useFilterSettings'
import { BOOKING_HISTORY_FILTER_KEY, FILTER_KEYS } from 'constants/filters'
import { TableHeadProps } from 'ui/molecules/Table/TableHead/types'
import { setFilterItems } from 'redux/filters/slice'
import {
  filterItemsSelector,
  selectedFiltersCountSelector,
  selectedFiltersSelector,
} from 'redux/filters/selectors'
import { sortingSelector } from 'redux/sorting/selectors'
import { Filter } from 'utils/addQueryString'
import { prepareFiltersForReq, prepareSortingForReq } from 'utils/table'
import { ResetFiltersButton } from 'ui/components/ResetFiltersButton'
import { usePagination } from 'hooks/usePagination'
import { ExportButton } from './components/ExportButton'

export const BookingHistoryList = () => {
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [bookingHistory, setBookingHistory] = useState<
    BookingHistoryTypes.GetBookingHistoryListResponse['reservations'] | null
  >(null)
  const filterItems = useSelector(filterItemsSelector)
  const selectedFiltersCount = useSelector(selectedFiltersCountSelector)
  const selectedFilters = useSelector(selectedFiltersSelector)
  const sortingData = useSelector(sortingSelector)
  const selectedRange = useSelector(selectedRangeSelector)
  const isOperator = useSelector(isOperatorSelector)

  const dispatch = useDispatch()
  const companyId = useCompanyId()

  const { page, pageSize, handleResetPagination } = usePagination()

  const getBookingHistoryListRequest = useApiRequest(
    (companyId: string, filters: Filter[], sorting: string) =>
      bookingHistoryApi.getBookingHistoryList(
        Number(pageSize),
        page - 1,
        companyId,
        filters,
        sorting
      )
  )

  const bookingHistoryFiltersRequest = useApiRequest((from, to) =>
    bookingHistoryApi.getBookingHistoryFiltersData(String(companyId), from, to)
  )

  const getFilterSettings = useFilterSettings(
    BOOKING_HISTORY_FILTER_KEY,
    handleResetPagination
  )

  const bookingHistoryRequestFilters = useMemo<Filter[]>(() => {
    let dateFilter: Filter[] = []

    if (selectedRange.from && selectedRange.to) {
      const toDate = new Date(selectedRange.to)
      const nextDay = addDays(toDate, 1)
      dateFilter = [
        {
          name: FILTER_KEYS.bookingHistory.dropoffdatetime,
          type: 'Ge',
          value: format(
            new Date(selectedRange.from),
            DATEPICKER_DEFAULT_FORMAT
          ),
        },
        {
          name: FILTER_KEYS.bookingHistory.dropoffdatetime,
          type: 'Lt',
          value: format(nextDay, DATEPICKER_DEFAULT_FORMAT),
        },
      ]
    } else {
      dateFilter = [
        {
          name: FILTER_KEYS.bookingHistory.dropoffdatetime,
          type: 'Ge',
          value: format(new Date(), DATEPICKER_DEFAULT_FORMAT),
        },
      ]
    }

    const initialFilters = prepareFiltersForReq(
      selectedFilters.bookingHistory,
      selectedFiltersCount.bookingHistory,
      BOOKING_HISTORY_FILTER_KEY
    )

    const formattedFilters = initialFilters.map((filter) => {
      if (filter.name === FILTER_KEYS.bookingHistory.reservationStatuses) {
        return {
          ...filter,
          value: BOOKING_STATUS[filter.value as keyof typeof BOOKING_STATUS],
        }
      }
      return filter
    })

    return [...formattedFilters, ...dateFilter]
  }, [
    selectedRange.from,
    selectedRange.to,
    selectedFilters.bookingHistory,
    selectedFiltersCount.bookingHistory,
  ])

  useEffect(() => {
    const fetchFilteredBookingHistory = async () => {
      if (bookingHistoryRequestFilters.length) {
        const auditListResponse = await getBookingHistoryListRequest.apiRequest(
          companyId,
          bookingHistoryRequestFilters,
          prepareSortingForReq(
            sortingData.bookingHistory,
            BOOKING_HISTORY_FILTER_KEY
          )
        )
        if (auditListResponse) {
          setBookingHistory(auditListResponse.data.reservations)
        }
      }
    }
    selectedRange?.from && selectedRange.to && fetchFilteredBookingHistory()
  }, [
    page,
    pageSize,
    selectedRange,
    sortingData.bookingHistory,
    bookingHistoryRequestFilters,
  ])

  const onOpenModal = () => {
    setIsFullScreen(true)
  }

  const onCloseModal = () => {
    setIsFullScreen(false)
  }

  const fetchBookingHistory = async () => {
    let from = ''
    let to = ''
    if (selectedRange.from && selectedRange.to) {
      from = selectedRange.from
      to = selectedRange.to
    }

    const bookingHistoryFilters = await bookingHistoryFiltersRequest.apiRequest(
      from,
      to
    )

    if (bookingHistoryFilters) {
      const filterItems = Object.entries(bookingHistoryFilters.data).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.map((el) => ({
            value: typeof el === 'string' ? el : el.reservationStatusName,
            selected: false,
          })),
        }),
        {}
      )

      dispatch(
        setFilterItems({ items: filterItems, key: BOOKING_HISTORY_FILTER_KEY })
      )
    }
  }

  useEffect(() => {
    !selectedFiltersCount.bookingHistory && fetchBookingHistory()
  }, [selectedRange, selectedFiltersCount.bookingHistory])

  const formattedBodyItems = useMemo(
    () =>
      bookingHistory?.pageItems.map((booking) => ({
        items: [
          booking.reservationNumber,
          booking.partnerNumber,
          booking.reservationStatus.reservationStatusName,
          booking.reservedDate,
          booking.partnerApproveDate,
          booking.pickUpLocationName,
          booking.pickUpDate,
          booking.dropOffLocationName,
          booking.dropOffDate,
          booking.duration,
          booking.netPrice,
          booking.companyCarClass,
          booking.sippCode,
          [...booking.prepaidExtras, ...booking.supplierExtras].map(
            (el, index) => (
              <Typography
                name="Subtitle7"
                className={styles.extra}
                key={index}
              >{`${el.price} ${el.currencyCode} - ${el.extraName} (x${el.quantity})`}</Typography>
            )
          ),
          booking.clientFirstName + ' ' + booking.clientLastName,
        ],
      })),
    [bookingHistory]
  )

  const daysPickerRange = useMemo(
    () => ({
      from: selectedRange.from ? new Date(selectedRange.from) : null,
      to: selectedRange.to ? new Date(selectedRange.to) : null,
    }),
    [selectedRange]
  )

  const handleSelectedRange = useCallback(
    (newValues: { from: Date; to: Date }) => {
      dispatch(
        setSelectedBookingHistoryRange({
          from: format(newValues.from, STOP_SALES_DATE_FORMAT),
          to: newValues.to ? format(newValues.to, STOP_SALES_DATE_FORMAT) : '',
        })
      )
    },
    []
  )

  const formattedHeadItems = useMemo(() => {
    const newHeadItems: TableHeadProps['item'][] = [...TABLE_HEAD_ITEMS]

    Object.entries(BOOKING_HISTORY_COLUMN_INDEX).forEach(([key, value]) => {
      newHeadItems[value].filterSettings = getFilterSettings(key)
    })

    return newHeadItems
  }, [getFilterSettings, filterItems.bookingHistory])

  const renderTable = () => (
    <>
      <div className={styles.list}>
        <div className={styles.header}>
          <ResetFiltersButton
            selectedFiltersCount={selectedFiltersCount.bookingHistory}
          />
          <ExportButton
            disabled={!bookingHistory?.pageItems.length}
            companyId={companyId}
            pageSize={pageSize}
            pageIndex={String(page - 1)}
            filters={bookingHistoryRequestFilters}
          />
          <div className={styles['date-picker-wrapper']}>
            <div className={styles['date-picker']}>
              <DaysPicker
                range={daysPickerRange}
                initialFrom={selectedRange.from}
                initialTo={selectedRange.to}
                size="small"
                isAvailableFutureDates
                handleRangeSelect={handleSelectedRange}
                variant="secondary"
              />
            </div>
            <Tooltip
              popupContent={BOOKING_HISTORY_TOOLTIP}
              size="primary"
              placement="bottom"
              alignSelf="center"
            >
              <InfoIcon color="blue700" size="small" />
            </Tooltip>
          </div>
          {isFullScreen ? (
            <ModalCloseIcon
              size="medium"
              color="blue700"
              onClick={onCloseModal}
              dataTestId="close-modal-button"
            />
          ) : (
            <MaximizeIcon
              color={
                bookingHistory?.pageItems.length === 0
                  ? 'blueSecondary'
                  : 'blue700'
              }
              size="large"
              onClick={onOpenModal}
              disabled={bookingHistory?.pageItems.length === 0}
              dataTestId="open-modal-button"
            />
          )}
        </div>
        <Table
          isStickyHeader
          headItems={formattedHeadItems}
          bodyItems={formattedBodyItems}
          initItemsWidth={prepareColumnsWidth()}
          dataTestId="booking-history-table"
        />
      </div>
      <footer className={styles.pagination}>
        <Pagination
          defaultPage={page}
          defaultPageSize={Number(pageSize)}
          pageItemCount={bookingHistory?.pageItems.length}
          total={bookingHistory?.totalItems || 0}
          dataTestId="booking-history-list-pagination"
          pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
        />
      </footer>
    </>
  )

  return (
    <>
      <Container
        title={MENU_NAMES.BOOKING_HISTORY}
        loading={!getBookingHistoryListRequest.isLoaded}
        breadcrumbList={isOperator ? [breadcrumbs.profileList] : []}
        currentPageLabel={MENU_NAMES.BOOKING_HISTORY}
        withNavigation={isOperator}
      >
        {renderTable()}
      </Container>
      {isFullScreen && (
        <Modal
          position="center-horizontal"
          offsetVariant="none"
          invisibleHeader={{ desktop: true }}
          invisibleClose={{ desktop: true }}
          dataTestId="table-modal"
        >
          <div className={styles['full-screen-modal']}>{renderTable()}</div>
        </Modal>
      )}
    </>
  )
}
